import React, { useState } from 'react';
import { Spin } from 'antd';
import { useNotifications } from '../../hooks/useNotifications';
import { useUnreadCount } from '../../hooks/useUnreadCount';
import { useMarkAsRead } from '../../hooks/useMarkAsRead';
import { useEnableNotifications } from '../../hooks/useEnableNotifications';
import { useDisableNotifications } from '../../hooks/useDisableNotifications';
import { Bell, Check, Settings } from 'lucide-react';
import { notifyUser } from '../../util/util';

function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

const NotificationsPage = () => {
  const { data: notificationsData, isLoading, error, refetch } = useNotifications();
  const { data: unreadCountData, refetch: refetchUnreadCount } = useUnreadCount();
  const markAsReadMutation = useMarkAsRead();
  const enableMutation = useEnableNotifications();
  const disableMutation = useDisableNotifications();

  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [localNotifications, setLocalNotifications] = useState([]);

  React.useEffect(() => {
    if (notificationsData?.data) setLocalNotifications(notificationsData.data);
  }, [notificationsData]);

  const notifications = localNotifications || [];
  // eslint-disable-next-line no-unused-vars
  const unreadCount = unreadCountData?.data || 0;

  // Primary handler for toggling read/unread state
  const handleToggleRead = (id) => {
    const prev = localNotifications;
    const currentItem = prev.find(n => n.id === id);
    if (!currentItem) return;

    const isMarkingAsRead = !currentItem.read_at;
    
    const updated = prev.map(n => 
      n.id === id ? { ...n, read_at: isMarkingAsRead ? new Date().toISOString() : null } : n
    );

    setLocalNotifications(updated);

    markAsReadMutation.mutate(id, {
      onSuccess: () => {
        refetch();
        refetchUnreadCount();
        notifyUser('success', isMarkingAsRead ? 'Notification marked as read' : 'Notification marked as unread');
      },
      onError: (err) => {
        setLocalNotifications(prev);
        notifyUser('error', err?.response?.data?.message || 'Failed to update notification status');
      }
    });
  };

  // Note: explicit single toggle handler `handleToggleRead` is used for both mark-as-read/unread

  const handleToggleNotifications = () => {
    if (notificationsEnabled) {
      disableMutation.mutate('', {
        onSuccess: () => {
          setNotificationsEnabled(false);
          notifyUser('success', 'Notifications disabled');
        },
        onError: (err) => notifyUser('error', err?.response?.data?.message || 'Failed to disable notifications'),
      });
    } else {
      enableMutation.mutate('', {
        onSuccess: () => {
          setNotificationsEnabled(true);
          notifyUser('success', 'Notifications enabled');
        },
        onError: (err) => notifyUser('error', err?.response?.data?.message || 'Failed to enable notifications'),
      });
    }
  };

  if (isLoading) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
  if (error) return <div style={{ padding: 40, textAlign: 'center', color: 'red' }}>Error loading notifications</div>;

  return (
    <div style={{ minHeight: '80vh', background: '#f5f6fa', padding: 24 }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: '#000' }}>Notifications</h1>
          <button
            onClick={handleToggleNotifications}
            style={{
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: 8,
              background: notificationsEnabled ? '#00b050' : '#fff',
              color: notificationsEnabled ? '#fff' : '#000',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <Settings size={16} />
            {notificationsEnabled ? 'Disable' : 'Enable'} Notifications
          </button>
        </div>

        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          {notifications.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#666' }}>
              <Bell size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
              <p>No notifications yet</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                style={{
                  padding: 20,
                  borderBottom: '1px solid #eee',
                  background: notification.read_at ? '#fff' : '#f0f8ff'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#000' }}>
                      {notification.data.title || 'Notification'}
                    </h3>
                    <p style={{ margin: '4px 0 8px 0', color: '#666', fontSize: 14 }}>
                      {notification.data.message}
                    </p>
                    <p style={{ margin: 0, color: '#999', fontSize: 12 }}>
                      {formatDate(notification.created_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggleRead(notification.id)}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #00b050',
                      borderRadius: 6,
                      background: notification.read_at ? '#fff' : '#00b050',
                      color: notification.read_at ? '#00b050' : '#fff',
                      cursor: 'pointer',
                      fontSize: 12,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontWeight: 600,
                      transition: 'all 0.2s'
                    }}
                  >
                    <Check size={12} />
                    {notification.read_at ? 'Mark as Unread' : 'Mark as Read'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;